import {translate} from 'views/module';

import userUtil from 'utils/miscellaneous/user/user_util';

const emptyParticipantDetail = {};
const clientRelationshipMarker = ':RELATIONSHIP-';

export const getParticipantRelationshipRn = (participantRn) => {
  return typeof participantRn === 'string' && participantRn.includes(clientRelationshipMarker) ?
    participantRn :
    null;
};

export const getParticipantResourceRn = (participantRn) => {
  const relationshipMarkerIndex = typeof participantRn === 'string' ?
    participantRn.indexOf(clientRelationshipMarker) :
    -1;

  if (relationshipMarkerIndex === -1) {
    return participantRn;
  }

  return participantRn.slice(0, relationshipMarkerIndex);
};

export const getParticipantRns = (caseData) => {
  return Array.isArray(caseData?.participants) ?
    caseData.participants :
    [caseData?.participants].filter(Boolean);
};

export const getCommunicationParticipantRns = (caseData) => {
  return getParticipantRns(caseData).reduce((result, participantRn) => {
    const participantResourceRn = getParticipantResourceRn(participantRn);

    if (!participantResourceRn || result.includes(participantResourceRn)) {
      return result;
    }

    return [
      ...result,
      participantResourceRn,
    ];
  }, []);
};

export const getParticipantRelationshipRnsByResourceRn = (caseData) => {
  return getParticipantRns(caseData).reduce((result, participantRn) => {
    const participantRelationshipRn = getParticipantRelationshipRn(participantRn);

    if (!participantRelationshipRn) {
      return result;
    }

    const participantResourceRn = getParticipantResourceRn(participantRn);
    const participantRelationshipRns = result[participantResourceRn] || [];

    if (participantRelationshipRns.includes(participantRelationshipRn)) {
      return result;
    }

    return {
      ...result,
      [participantResourceRn]: [
        ...participantRelationshipRns,
        participantRelationshipRn,
      ],
    };
  }, {});
};

export const getParticipantKind = (participantRn) => {
  const participantResourceRn = getParticipantResourceRn(participantRn);

  if (/^RN:CLIENT-V1-/.test(participantResourceRn)) {
    return 'CLIENT';
  }

  if (/^RN:CAREGIVER-V1-/.test(participantResourceRn)) {
    return 'CAREGIVER';
  }

  return 'OTHER';
};

export const getParticipantType = (participantRn) => {
  if (getParticipantRelationshipRn(participantRn)) {
    return translate('case.Communication.ParticipantTypes.CLIENT_RELATIONSHIP');
  }

  return translate(`case.Communication.ParticipantTypes.${getParticipantKind(participantRn)}`);
};

export const getParticipantDetail = (participantDetails, participantRn) => {
  const participantResourceRn = getParticipantResourceRn(participantRn);

  return participantDetails[participantResourceRn]?.detail || emptyParticipantDetail;
};

export const getParticipantName = (participantDetails, participantRn) => {
  const participantDetail = getParticipantDetail(participantDetails, participantRn);
  const participantRelationshipRn = getParticipantRelationshipRn(participantRn);
  const relationshipName = participantRelationshipRn ?
    participantDetail.relationships?.[participantRelationshipRn]?.name :
    '';
  const displayName = participantDetail.clientName ||
    participantDetail.caregiverName ||
    userUtil.getFullName(participantDetail.firstName, participantDetail.lastName).trim();

  return relationshipName || displayName || participantRn;
};

export const methodOptions = [
  {key: 'ALL', translationKey: 'All'},
  {key: 'INTERNAL', translationKey: 'Internal'},
  {key: 'EMAIL', translationKey: 'Email'},
  {key: 'SMS', translationKey: 'Sms'},
  {key: 'CALL', translationKey: 'Call'},
  {key: 'CHAT', translationKey: 'Chat'},
];

export const supportedParticipantKinds = ['CLIENT', 'CAREGIVER'];

export const getMessageTitle = (messageType) => {
  const messageTitle = translate(`case.Communication.MessageTypes.${messageType}`);

  return messageTitle === 'Missing translation' ?
    translate('case.Communication.MessageTypes.UNKNOWN') :
    messageTitle;
};

export const getFromName = (user) => {
  return userUtil.getFullName(user?.firstName, user?.lastName).trim();
};

export const getRelationshipOptions = (participantDetail) => {
  return Object.values(participantDetail?.relationships || {})
      .filter((relationship) => relationship?.relationshipRn)
      .sort((firstRelationship, secondRelationship) => {
        return (firstRelationship.priority ?? Number.MAX_SAFE_INTEGER) - (secondRelationship.priority ?? Number.MAX_SAFE_INTEGER);
      })
      .map((relationship) => ({
        key: relationship.relationshipRn,
        label: relationship.name || relationship.relationshipRn,
        relationship,
      }));
};

export const getRelationshipContact = (relationship, type) => {
  return relationship?.contacts?.find((contact) => contact.type === type)?.detail || '';
};

export const createLocalSmsMessage = (newMessage, user) => {
  const createdAt = new Date().toISOString();
  const senderName = getFromName(user);

  return {
    messageRn: `LOCAL-SENT-${createdAt}-${Math.random().toString(36).slice(2, 10)}`,
    messageType: 'DIALPAD_SMS_TEXT',
    body: {
      blob: `${senderName}: ${newMessage}`,
      editorType: 'MARKDOWN',
    },
    source: 'WEBSITE',
    sentBy: 'ADMIN',
    createdAt,
    updatedAt: createdAt,
    submittedBy: {
      type: 'EXTERNAL',
      resourceRn: 'RN:SERVER-V1-DIALPADINTEGRATION',
    },
    emoji: {},
    emojisWithRequesterData: {},
    isLocalOnly: true,
  };
};

export const getCreatedMessage = (output) => {
  const message = output?.message || output?.result?.message;
  const messageRn = output?.messageRn || output?.result?.messageRn || message?.messageRn;

  if (!message || !messageRn) return null;

  return {
    ...message,
    messageRn,
  };
};

export const getMessageMethod = (messageType) => {
  switch (messageType) {
    case 'ANNOUNCEMENTS':
    case 'COMMENTS':
      return 'INTERNAL';
    case 'EMAIL':
      return 'EMAIL';
    case 'DIALPAD_SMS_TEXT':
      return 'SMS';
    case 'DIALPAD_CALL_TRANSCRIPT':
      return 'CALL';
    default:
      return 'OTHER';
  }
};

export const sortMessages = (messages) => {
  return [...messages].sort((firstMessage, secondMessage) => {
    return new Date(secondMessage.createdAt) - new Date(firstMessage.createdAt);
  });
};

export const sortChatMessages = (messages) => {
  return [...messages].sort((firstMessage, secondMessage) => {
    return new Date(secondMessage.createdAt) - new Date(firstMessage.createdAt);
  });
};

const getChatParticipantLabel = (participant) => {
  return userUtil.getFullName(participant?.firstName, participant?.lastName).trim() ||
    participant?.name ||
    participant?.userRn;
};

export const getChatConversationTitle = (conversation, caregiverRn) => {
  if (conversation?.conversationRn?.includes('SUPPORT')) {
    return translate('case.Communication.Chat.SupportChat');
  }

  const participants = (conversation?.participants || []).filter((participant) => {
    return participant.userRn !== caregiverRn;
  });
  const participantNames = participants.map(getChatParticipantLabel).filter(Boolean);

  return participantNames.join(', ') || translate('case.Communication.Chat.UnknownConversation');
};

export const getLatestChatMessageText = (conversation) => {
  const latestMessage = conversation?.latestMessage;

  if (latestMessage?.messageText) {
    return latestMessage.messageText;
  }

  if (latestMessage?.files?.length > 0) {
    return translate('case.Communication.Chat.Attachment');
  }

  return translate('case.Communication.Chat.NoMessages');
};

export const sortChatConversations = (conversations) => {
  return [...conversations].sort((firstConversation, secondConversation) => {
    const firstTime = firstConversation.latestMessage?.createdAt ? new Date(firstConversation.latestMessage.createdAt).getTime() : 0;
    const secondTime = secondConversation.latestMessage?.createdAt ? new Date(secondConversation.latestMessage.createdAt).getTime() : 0;

    return secondTime - firstTime;
  });
};
